import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { ErpContext } from "../../context/ErpContext";

const CrearLoteModal = ({ open, onClose, onLoteCreado, itemId, ubicaciones }) => {
  const { postLote, updateLoteItemUbicacionStock } = useContext(ErpContext);

  const [numeroLote, setNumeroLote] = useState("");
  const [fechaFabricacion, setFechaFabricacion] = useState("");
  const [fechaVencimiento, setFechaVencimiento] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [stockInicial, setStockInicial] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setNumeroLote("");
    setFechaFabricacion("");
    setFechaVencimiento("");
    setObservaciones("");
    setStockInicial({});
    setError(null);
    setSuccess(false);
  };

  const handleStockChange = (ubicacionId, value) => {
    setStockInicial({
      ...stockInicial,
      [ubicacionId]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validaciones
    if (!numeroLote.trim()) {
      setError("El número de lote es requerido");
      return;
    }

    setIsLoading(true);

    try {
      // 1. Crear el lote
      const loteResponse = await postLote({
        idItem: itemId,
        numeroLote: numeroLote.trim(),
        fechaFabricacion: fechaFabricacion || null,
        fechaVencimiento: fechaVencimiento || null,
        observaciones: observaciones.trim() || null,
      });

      const loteId = loteResponse.lote.id;

      // 2. Guardar stock inicial en ubicaciones (si se especificó)
      const ubicacionesConStock = Object.entries(stockInicial).filter(
        ([_, stock]) => stock && parseFloat(stock) > 0
      );

      for (const [ubicacionId, stock] of ubicacionesConStock) {
        await updateLoteItemUbicacionStock({
          idLote: loteId,
          idUbicacion: parseInt(ubicacionId),
          stock: parseFloat(stock),
        });
      }

      setSuccess(true);
      setTimeout(() => {
        onLoteCreado();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error("Error al crear lote:", err);
      setError(
        err.response?.data?.message || 
        "Error al crear el lote. Verifica que el número no exista."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#5b5b5f",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" component="div">
          Crear Nuevo Lote
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={isLoading}
          sx={{ color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              ¡Lote creado exitosamente!
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Número de Lote */}
            <Grid item xs={12}>
              <TextField
                label="Número de Lote *"
                fullWidth
                value={numeroLote}
                onChange={(e) => setNumeroLote(e.target.value)}
                disabled={isLoading || success}
                required
                placeholder="Ej: L-2025-10-001"
                helperText="Identificador único del lote"
              />
            </Grid>

            {/* Fecha de Fabricación */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de Fabricación"
                type="date"
                fullWidth
                value={fechaFabricacion}
                onChange={(e) => setFechaFabricacion(e.target.value)}
                disabled={isLoading || success}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Fecha de fabricación o recepción"
              />
            </Grid>

            {/* Fecha de Vencimiento */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de Vencimiento"
                type="date"
                fullWidth
                value={fechaVencimiento}
                onChange={(e) => setFechaVencimiento(e.target.value)}
                disabled={isLoading || success}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Fecha de vencimiento del lote"
              />
            </Grid>

            {/* Observaciones */}
            <Grid item xs={12}>
              <TextField
                label="Observaciones"
                fullWidth
                multiline
                rows={2}
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                disabled={isLoading || success}
                placeholder="Información adicional sobre el lote"
              />
            </Grid>

            {/* Stock inicial por ubicación */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, mt: 2, fontWeight: 600 }}
              >
                Stock Inicial por Ubicación (Opcional)
              </Typography>
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: "#f9f9f9",
                }}
              >
                <Grid container spacing={2}>
                  {ubicaciones.map((ubicacion) => (
                    <Grid item xs={12} sm={6} key={ubicacion.id}>
                      <TextField
                        label={ubicacion.descripcion}
                        type="number"
                        fullWidth
                        size="small"
                        value={stockInicial[ubicacion.id] || ""}
                        onChange={(e) =>
                          handleStockChange(ubicacion.id, e.target.value)
                        }
                        disabled={isLoading || success}
                        inputProps={{
                          min: 0,
                          step: "0.01",
                        }}
                        placeholder="0"
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            sx={{ color: "#666" }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || success || !numeroLote.trim()}
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            sx={{
              backgroundColor: "rgb(244, 147, 14)",
              "&:hover": {
                backgroundColor: "rgb(220, 130, 10)",
              },
            }}
          >
            {isLoading ? "Creando..." : "Crear Lote"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CrearLoteModal;


